import React, { useState } from 'react'
import PropTypes from 'prop-types'
import tw, { css, styled } from 'twin.macro'
import { Link, graphql } from 'gatsby'
import qs from 'qs'

import Layout from '../../components/layout'
import Meta from '../../components/meta'
import filterLocale from '../../components/utils/filter-locale'
import SupportSearch from '../../components/support/support-search'
import Tabs from '../../components/support/tabs'
import {
	ContentsSection,
	ContentsContainer,
	TextsImageContainer,
	Header,
	HeaderSubText,
	TextsContainer,
} from '../../components/dealer-search/styles'
import TypeInput from '../../components/dealer-search/type-input'
import AddressInput from '../../components/dealer-search/address-input'
import MapBox from '../../components/dealer-search/map-box'
import {
	useDealerSearch,
	useAddressSearch,
} from '../../components/dealer-search/utils'
import {
	ListboxInput,
	ListboxButton,
	ListboxList,
	ListboxOption,
	ListboxPopover,
} from '@reach/listbox'
import '@reach/listbox/styles.css'
import spares from '../../spares/spares-2025.json'
const dealerTypes = [
	{ name: <span>Robotic Repairs <strong>(WARRANTY)</strong></span>, type: 'roboticrepairs' , warranty: true},
	{ name: <span>Robotic Repairs <strong>(OUT OF WARRANTY)</strong></span>, type: 'roboticrepairs', warranty: false },
	{ name: 'Water Treatment Repairs', type: 'watertreatmentrepairs' },
	{ name: 'Commercial Repairs', type: 'commercial' },
	{ name: 'Spare Parts', type: { $in: ['elite','roboticrepairs'] } },
]

const BuyParts = styled(Link)`
	${tw`bg-yellow-500 hover:bg-yellow-400 font-display font-bold uppercase py-3 px-6 rounded mt-8 w-48 text-center`}
`
const ImageContainer = styled.div`
	${tw`md:mr-0 md:ml-auto mt-4 md:mt-0`}

	> img {
		${tw`w-6/12 md:w-10/12 md:mr-auto md:mr-0 md:ml-auto`}
	}

	@media only screen and (max-width: 767px) {
		display: flex;
		justify-content: center;
	}
`
const Popover = styled(ListboxPopover)`
	${tw`font-display text-sm`}
	z-index: 100;
`
const TableHolder = tw.div`overflow-auto mb-12`
const Table = styled.table`
	${tw`w-full table-auto mt-4`}

	> tbody > tr > th {
		${tw`font-display border px-4 py-2 w-1/5`}
	}
	> tbody > tr > td {
		min-width: 200px;
		${tw`font-display border px-4 py-2 text-center`}
		> a {
			${tw`underline text-blue-500 hover:no-underline`}
		}
	}
`
const FieldsContainer = styled.div`
	${tw`flex flex-col md:flex-row items-start md:items-center`}

	> div {
		${tw`flex flex-col md:flex-row mx-0 md:mx-2`}
	}

	.searchBar {
		${tw`mt-4 md:mt-0 p-2 border-2 border-black rounded`}
	}
`

const RepairsAndParts = ({ data: { repairparts }, location: { search } }) => {
	const locale = 'en-AU'
	const [{ node }] = filterLocale(repairparts.edges, locale)

	const { seoTitle, seoKeywords, seoDescription } = node

	const [queryAddress, onQueryAddress] = useState('')
	const [selectAddress, onSelectAddress] = useState({
		name: '',
		coordinates: [],
	})
	const [selectType, setSelectType] = useState(dealerTypes[0])
	const addresses = useAddressSearch(queryAddress, 'en-AU')
	const [dealer, setDealer] = useState({})
	const [selectDealer, onSelectDealer] = useState(0)

	let { dealers, loading } = useDealerSearch(
		selectType.type,
		selectAddress.coordinates,
		setDealer,
		'en-AU',
		6
	)

	// Filter out dealers with "maytronics" in name when type is "Spare Parts"
	const filteredDealers =
		selectType.name === 'Spare Parts' && dealers && dealers.length > 0
			? dealers.filter(
					dealer =>
						!dealer.name ||
						!dealer.name.toLowerCase().includes('maytronics')
			  )
			: dealers

	// Get current dealer from filtered list (always use first if current is filtered out)
	const currentDealer =
		filteredDealers &&
		filteredDealers.length > 0 &&
		(!dealer._id || !filteredDealers.some(d => d._id === dealer._id))
			? filteredDealers[0]
			: dealer || (filteredDealers && filteredDealers[0]) || {}

	// Handle type change and update dealer selection if needed
	const handleSelectType = newType => {
		setSelectType(newType)
		// When switching to "Spare Parts", select first filtered dealer
		if (newType.name === 'Spare Parts' && filteredDealers && filteredDealers.length > 0) {
			setDealer(filteredDealers[0])
			onSelectDealer(0)
		}
	}

	const handleSelectDealer = i => {
		setDealer(filteredDealers[i])
		onSelectDealer(i)
	}

	if (selectType.type === "roboticrepairs") {
		const criterion = dealer => dealer.name.toLowerCase().includes('maytronics')
		let processedDealers = filteredDealers.sort((a, b) => {
			if (criterion(a) && !criterion(b)) {
				return -1;
			} else if (!criterion(a) && criterion(b)) {
				return 1
			} else {
				return 0;
			}
		});
		if (!selectType.warranty){
			processedDealers = processedDealers.filter(dealer => !dealer.name.toLowerCase().includes('maytronics'))
		}
		processedDealers = processedDealers.filter(dealer => {
			if (!dealer.branch) {
				return true;
			}
			return !dealer.branch.is_busy;
		});
		dealers = processedDealers.slice(0, 3)
	} else {
		dealers = filteredDealers.slice(0, 3)
	}

	const [keyword, setKeyword] = useState('')
	const [value, setValue] = useState('Part Description')
	const [res, setRes] = useState(10)

	const x =
		keyword != '' && value != 'default'
			? spares.filter(x => {
					const word = x[value].includes(keyword)
					return word
			  })
			: false

	return (
		<Layout>
			<Meta
				title={seoTitle}
				keywords={seoKeywords}
				description={seoDescription}
			/>
			<SupportSearch />
			<Tabs locale={locale} />
			<ContentsSection>
				<ContentsContainer>
					<TextsImageContainer>
						<TextsContainer>
							<Header>Find an authorised Maytronics repairer</Header>
							<HeaderSubText>
								Keep your Dolphin in top condition by using a trusted Maytronics
								Authorised Service Centre that uses genuine Maytronics-approved
								spare parts, to ensure the highest quality repairs for your
								Dolphin.
							</HeaderSubText>
							<BuyParts to="/shop">Buy Parts</BuyParts>
						</TextsContainer>

						<ImageContainer>
							<img
								src="//images.ctfassets.net/ftcnv5up9dxv/OGmUM25A1ZqSWRQWRiXXk/8773e386dc7bd3d5490e0be319c0c8fd/Maytronics-Authorised-Service-Centre.jpg"
								alt="Maytronics-Authorised-Service-Centre"
							/>
						</ImageContainer>
					</TextsImageContainer>

					<div tw="flex flex-row w-full flex-wrap items-center justify-center font-display pb-6">
						<div tw="flex flex-col md:flex-row md:space-x-4 items-end justify-center w-full h-full text-gray-800">
							<div tw="w-full md:w-64 md:-mb-3">
								<AddressInput
									{...{
										queryAddress,
										onQueryAddress,
										addresses,
										selectAddress,
										onSelectAddress,
										placeholder: 'address',
									}}
								/>
							</div>
							<div tw="w-full md:w-80 -mb-3">
								<TypeInput
									{...{
										dealerTypes,
										selectType,
										onSelectType: handleSelectType,
									}}
								/>
							</div>
						</div>
					</div>

					<div tw="flex flex-row w-full items-center justify-center pb-8">
						<div tw="w-full">
							<MapBox
								{...{
									loading,
									selectAddress,
									selectType,
									dealers,
									selectDealer:
										dealers && currentDealer._id
											? dealers.findIndex(d => d._id === currentDealer._id)
											: 0,
									handleSelectDealer,
									locale: 'en-AU',
									full: true,
								}}
								tw="mb-0"
							/>
						</div>
					</div>

					<section
						tw="grid grid-rows-3 md:grid-cols-3 md:grid-rows-none gap-1"
						css={[dealers && dealers[0] && dealers[0].name && tw`pb-8`]}
					>
						{dealers &&
							dealers[0] &&
							dealers[0].name &&
							dealers.map((dealer, i) => (
								<div
									tw="inline-flex items-start p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
									css={[selectDealer === i && tw`bg-gray-200`]}
									key={dealer._id}
									onClick={() => handleSelectDealer(i)}
									onKeyDown={() => handleSelectDealer(i)}
									role="button"
									tabIndex="-1"
								>
									<div tw="inline-block w-full rounded-md">
										<div tw="cursor-default relative flex flex-col space-y-1 w-full rounded-md p-2 text-left sm:text-sm sm:leading-5">
											{dealer.logo && (
												<img
													id="dealer-logo"
													tw="w-auto mx-auto mb-2 object-contain max-h-[100px]"
													alt={dealer.name}
													src={dealer.logo}
												/>
											)}
											<h3 tw="mb-2 font-display font-bold text-base text-gray-700">
												{dealer.name}
											</h3>
											<h4 tw="mb-2 font-display text-sm text-gray-700">
												{`${dealer.address.city} ${dealer.address.postalCode}`}
											</h4>
											<div tw="mb-2 font-display font-bold text-sm text-blue-500 leading-6">
												{dealer.phone}
											</div>
											<div tw="mb-2 font-display font-bold text-sm text-blue-500 leading-6">
												{dealer.email && dealer.email[0]}
											</div>
											{dealer.type === 'elite' && (
												<div
													className="button-group"
													tw="inline-flex space-x-2 mr-auto"
												>
													<a
														tw="inline-flex bg-yellow-500 rounded px-4 py-2 shadow-md font-display font-semibold focus:outline-none focus:shadow-outline-yellow focus:border-yellow-300 transition ease-in-out duration-150 "
														href={
															'https://mydolphin.com.au/' +
															dealer.social.minisite
														}
														target="_blank"
														rel="noreferrer noopener"
													>
														View Profile
													</a>
												</div>
											)}
										</div>
									</div>
								</div>
							))}
					</section>

					<Header>Find a Part</Header>

					<FieldsContainer>
						<div>
							<p id="my-label">Choose One</p>
							<ListboxInput
								onChange={value => {
									setValue(value)
								}}
							>
								<ListboxButton arrow="▼" />
								<Popover>
									<ListboxList>
										<ListboxOption value="default">--Select--</ListboxOption>
										<ListboxOption value="Part Number">
											Part Number
										</ListboxOption>
										<ListboxOption value="Part Description">
											Part Description
										</ListboxOption>
										<ListboxOption value="Family Description">
											Family Description
										</ListboxOption>
										<ListboxOption value="Price exGST">
											Price exGST
										</ListboxOption>
										<ListboxOption value="Price incGST">
											Price incGST
										</ListboxOption>
									</ListboxList>
								</Popover>
							</ListboxInput>
						</div>

						<div>
							<p id="my-label">Number of Results</p>
							<ListboxInput
								onChange={res => {
									setRes(res)
								}}
							>
								<ListboxButton arrow="▼" />
								<Popover>
									<ListboxList>
										<ListboxOption value="10">10</ListboxOption>
										<ListboxOption value="20">20</ListboxOption>
									</ListboxList>
								</Popover>
							</ListboxInput>
						</div>

						<input
							className="searchBar"
							type="text"
							value={keyword}
							placeholder="Search"
							onChange={e => {
								setKeyword(e.target.value)
							}}
						/>
					</FieldsContainer>

					<TableHolder>
						<Table>
							<tbody>
								<tr>
									<th>Part Number</th>
									<th>Part Description</th>
									<th>Family Description</th>
									<th>Price exGST</th>
									<th>Price incGST</th>
								</tr>
								{keyword === '' ? (
									spares.slice(0, res).map(x => {
										return (
											<tr key={x['Part Number']}>
												<td>{x['Part Number']}</td>
												<td>{x['Part Description']}</td>
												<td>{x['Family Description']}</td>
												<td>{x['Price exGST']}</td>
												<td>{x['Price incGST']}</td>
											</tr>
										)
									})
								) : keyword != '' && value != 'default' ? (
									x.length != 0 ? (
										x.slice(0, res).map(x => {
											return (
												<tr key={x['Part Number']}>
													<td>{x['Part Number']}</td>
													<td>{x['Part Description']}</td>
													<td>{x['Family Description']}</td>
													<td>{x['Price exGST']}</td>
													<td>{x['Price incGST']}</td>
												</tr>
											)
										})
									) : (
										<tr>
											<td colSpan="5">
												No Result for this query, Please try again.
											</td>
										</tr>
									)
								) : (
									<tr>
										<td colSpan="5">Select is empty, Please Choose One.</td>
									</tr>
								)}
							</tbody>
						</Table>
					</TableHolder>
				</ContentsContainer>
			</ContentsSection>
		</Layout>
	)
}
/**
 * Page content query
 * @private
 * @static
 * @returns {() => void}
 */
export const staticQuery = graphql`
	query repairPartsQuery {
		repairparts: allContentfulPage(
			filter: { contentful_id: { eq: "2JhtBK7Tx60VtNIKeD6eoL" } }
		) {
			edges {
				node {
					seoTitle
					seoKeywords
					seoDescription
					node_locale
					slug
					body {
						json
					}
				}
			}
		}
	}
`
RepairsAndParts.propTypes = {
	data: PropTypes.shape({
		repairparts: PropTypes.objectOf(PropTypes.array),
	}),
	location: PropTypes.shape({
		search: PropTypes.string,
	}),
}

export default RepairsAndParts
